import { cn } from '../../styles/lib/utils';
import { IconButton } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { getPageRange } from '../Layers/pageRange';
import { SkeletonSpinner } from '../SkeletonLoader';
import { Text } from '../Text';

export interface TablePaginationProps {
  /** 1-based current page. */
  currentPage: number;
  /** Total rows across all pages (post-filter, if any). */
  totalItems: number;
  pageSize: number;
  /** Called with the 1-based target page when prev/next is clicked. */
  onPageChange: (page: number) => void;
  /** Shows a skeleton in place of the range text while true. */
  isLoading?: boolean;
  className?: string;
  'data-testid'?: string;
  /** Overrides the derived `${data-testid}-prev` id, for a caller with an existing testid to preserve. */
  prevTestId?: string;
  /** Overrides the derived `${data-testid}-next` id, for a caller with an existing testid to preserve. */
  nextTestId?: string;
}

/**
 * Prev/next page navigation + a "start-end of total" range indicator.
 *
 * Extracted from `TableActionRow`, which still uses it internally; also composed
 * directly by `Table` via its `showPagination` prop.
 */
function TablePagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  isLoading = false,
  className,
  'data-testid': dataTestId,
  prevTestId,
  nextTestId,
}: TablePaginationProps) {
  const { start, end, totalPages, clampedPage } = getPageRange(currentPage, pageSize, totalItems);

  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  return (
    <div className={cn('flex items-center gap-2', className)} data-testid={dataTestId}>
      <Text className="text-2xs text-primary" data-testid={testId('range')}>
        {isLoading ? <SkeletonSpinner size={14} /> : `${start}-${end} of ${totalItems.toLocaleString()}`}
      </Text>
      <ButtonGroup>
        <IconButton
          icon="chevronLeft"
          variant="secondary"
          size="icon"
          aria-label="Previous page"
          disabled={clampedPage === 1}
          onClick={() => onPageChange(clampedPage - 1)}
          data-testid={prevTestId ?? testId('prev')}
        />
        <IconButton
          icon="chevronRight"
          variant="secondary"
          size="icon"
          aria-label="Next page"
          disabled={clampedPage === totalPages}
          onClick={() => onPageChange(clampedPage + 1)}
          data-testid={nextTestId ?? testId('next')}
        />
      </ButtonGroup>
    </div>
  );
}

export { TablePagination };
